/* eslint-disable unicorn/prefer-module */
import { useCallback, useMemo, useState } from "react";
import { FaInfoCircle } from "react-icons/fa";
import { ImCheckboxChecked, ImCheckboxUnchecked } from "react-icons/im";
import cx from "classix";
import { BadgeDato, CategoriaLeyendaIpsos, Loader } from "..";
import { ipsos } from "../../data/ipsos2025";
import {
  ESCENARIOS_CAPTACION_TRANSPORTE,
  FRECUENCIA_USO,
  IPSOS_TRANSPORTE,
  PRECIOS_VIAJE,
} from "../../data/ipsos2025/transporte";
import { useCategoriaMoneda, useUniversoCategorias } from "../../utils";
import { Header } from "../Header";
import styles from "./CategoriaTransporte.module.scss";

export const Transporte = () => {
  const { formatNumber, formatLocal, convertCLP, pais } = useCategoriaMoneda();
  const {
    edificios,
    loading,
    error,
    checkAll,
    checkedBuildings,
    toggleCheck,
    toggleCheckAll,
    cantidadEdificios,
    totalPantallas,
    totalEjecutivos,
    totalEmpresas,
  } = useUniversoCategorias(pais);
  const [showInventarioModal, setShowInventarioModal] = useState(false);
  const [verAnual, setVerAnual] = useState(false);
  const [showOportunidadModal, setShowOportunidadModal] = useState(false);

  // Estado para simulador
  const [appSeleccionada, setAppSeleccionada] = useState("Uber");
  const [tipoViaje, setTipoViaje] = useState("medio");
  const [porcentajeCaptacion, setPorcentajeCaptacion] = useState(ESCENARIOS_CAPTACION_TRANSPORTE[1].valor);

  // =====================================================
  // CÁLCULOS DE APPS DE TRANSPORTE
  // =====================================================
  const resultados = useMemo(() => {
    if (!totalEjecutivos) return null;

    const usanApps = Math.round(totalEjecutivos * IPSOS_TRANSPORTE.usaApps.valor);
    const noUsanApps = Math.round(totalEjecutivos * IPSOS_TRANSPORTE.noUsaApps.valor);

    // Usuarios por app instalada — base 400 = P (lámina 106), no usanApps
    const usuariosPorApp = {};
    Object.entries(IPSOS_TRANSPORTE.appsInstaladas).forEach(([app, dato]) => {
      usuariosPorApp[app] = Math.round(totalEjecutivos * dato.valor);
    });

    // Preferencia de app — base 400 = P (lámina 106), no usanApps
    const preferenciaPorApp = {};
    Object.entries(IPSOS_TRANSPORTE.appPreferida).forEach(([app, dato]) => {
      preferenciaPorApp[app] = Math.round(totalEjecutivos * dato.valor);
    });

    // Calcular viajes mensuales por frecuencia de uso
    let totalViajesMes = 0;
    const desgloseFrecuencia = {};

    Object.entries(FRECUENCIA_USO).forEach(([key, data]) => {
      const usuarios = Math.round(usanApps * data.pct.valor);
      const viajes = usuarios * data.viajesMes.valor;
      desgloseFrecuencia[key] = {
        label: data.label,
        usuarios,
        viajesMes: Math.round(viajes),
      };
      totalViajesMes += viajes;
    });

    // Calcular mercado por app (usando precio medio como referencia)
    // El reparto de viajes se normaliza entre las 3 apps con preferencia (79.8+6.9+5.1=91.8),
    // sin descontar "Ninguna" dos veces (ya está descontada en totalViajesMes vía usanApps)
    const appsConPreferencia = Object.entries(IPSOS_TRANSPORTE.appPreferida).filter(([app]) => app !== "Ninguna");
    const totalShareApps = appsConPreferencia.reduce((sum, [, dato]) => sum + dato.valor, 0);

    const mercadoPorApp = {};
    appsConPreferencia.forEach(([app, dato]) => {
      const shareNormalizado = dato.valor / totalShareApps;
      const viajesApp = totalViajesMes * shareNormalizado;
      const precioMedio = PRECIOS_VIAJE[app]?.medio.valor || 6500;
      mercadoPorApp[app] = {
        shareDato: ipsos(shareNormalizado, {
          lamina: 106,
          pregunta: "¿Cuál es su app de transporte preferida o más usada?",
          base: 400,
          nota: "normalizado sobre appPreferida, excluye 'Ninguna'",
        }),
        share: shareNormalizado,
        viajesMes: Math.round(viajesApp),
        mercadoMensual: Math.round(viajesApp * precioMedio),
      };
    });

    // Mercado total
    const mercadoTotalMensual = Object.values(mercadoPorApp).reduce((sum, app) => sum + app.mercadoMensual, 0);

    return {
      usanApps,
      noUsanApps,
      usuariosPorApp,
      preferenciaPorApp,
      desgloseFrecuencia,
      totalViajesMes: Math.round(totalViajesMes),
      totalViajesAnual: Math.round(totalViajesMes * 12),
      mercadoPorApp,
      mercadoTotalMensual,
      mercadoTotalAnual: mercadoTotalMensual * 12,
    };
  }, [totalEjecutivos]);

  // =====================================================
  // CÁLCULO OPORTUNIDAD
  // =====================================================
  const calcularOportunidad = useCallback(() => {
    if (!resultados) return null;

    const datosApp = resultados.mercadoPorApp[appSeleccionada];
    if (!datosApp) return null;

    const precioViaje = PRECIOS_VIAJE[appSeleccionada][tipoViaje].valor;
    const mercadoCaptadoMensual = Math.round(resultados.mercadoTotalMensual * (porcentajeCaptacion / 100));
    const viajesCaptados = Math.round(mercadoCaptadoMensual / precioViaje);

    return {
      mercadoTotal: resultados.mercadoTotalMensual,
      mercadoCaptadoMensual,
      mercadoCaptadoAnual: mercadoCaptadoMensual * 12,
      viajesCaptadosMes: viajesCaptados,
      precioViaje,
    };
  }, [resultados, appSeleccionada, tipoViaje, porcentajeCaptacion]);

  if (loading) return <Loader />;
  if (error) return <p className={styles.disclaimer}>{error}</p>;

  return (
    <div className={styles.transportePage}>
      <Header leftContent={<h1 className={styles.headerTitulo}>APPS DE TRANSPORTE</h1>}>
        <div className={styles.inventarioHeader}>
          <div className={styles.inventarioInfo}>
            <span>
              <strong>{cantidadEdificios}</strong> edificios
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalPantallas)}</strong> pantallas
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalEjecutivos)}</strong> personas
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalEmpresas)}</strong> empresas
            </span>
          </div>
          <button type="button" className={styles.inventarioBtn} onClick={() => setShowInventarioModal(true)}>
            Inventario
          </button>
        </div>
      </Header>

      <CategoriaLeyendaIpsos />

      <div className={styles.transporteContent}>
        {/* Resumen de penetración */}
        {resultados && (
          <div className={styles.resumenPenetracion}>
            <div className={styles.resumenCard}>
              <div className={styles.resumenValor}>
                <BadgeDato dato={IPSOS_TRANSPORTE.usaApps}>
                  {(IPSOS_TRANSPORTE.usaApps.valor * 100).toFixed(0)}%
                </BadgeDato>
              </div>
              <div className={styles.resumenLabel}>Usa apps de transporte</div>
              <div className={styles.resumenNumero}>{formatNumber(resultados.usanApps)} personas</div>
            </div>
            <div className={styles.resumenCard}>
              <div className={styles.resumenValor}>
                <span className={styles.valorDestacado}>{formatNumber(resultados.totalViajesMes)}</span>
                <span className={styles.tooltipContainer}>
                  <FaInfoCircle className={styles.infoIcon} />
                  <span className={styles.tooltipText}>
                    Estimación basada en frecuencia de uso: 15% intensivo, 25% frecuente, 35% moderado, 25% ocasional.
                  </span>
                </span>
              </div>
              <div className={styles.resumenLabel}>Viajes estimados / mes</div>
              <div className={styles.resumenDetalle}>{formatNumber(resultados.totalViajesAnual)} viajes/año</div>
            </div>
            <div className={styles.resumenCard}>
              <div className={styles.resumenValor}>
                <span className={styles.valorDestacado}>{formatLocal(convertCLP(resultados.mercadoTotalMensual))}</span>
              </div>
              <div className={styles.resumenLabel}>Mercado mensual</div>
              <div className={styles.resumenDetalle}>{formatLocal(convertCLP(resultados.mercadoTotalAnual))}/año</div>
            </div>
          </div>
        )}

        {/* Sección Apps Instaladas y Preferencia */}
        {resultados && (
          <div className={styles.appsSection}>
            {/* Apps Instaladas */}
            <div className={styles.appsCard}>
              <h4 className={styles.subtitulo}>Apps Instaladas</h4>
              <div className={styles.appsList}>
                {Object.entries(IPSOS_TRANSPORTE.appsInstaladas)
                  .filter(([app]) => app !== "Ninguna" && app !== "Otra" && app !== "Tranvip")
                  .map(([app, dato]) => (
                    <div key={app} className={styles.appItem}>
                      <div className={styles.appNombre}>{app}</div>
                      <div className={styles.appBar}>
                        <div className={styles.appBarFill} style={{ width: `${dato.valor * 100}%` }} />
                      </div>
                      <div className={styles.appStats}>
                        <BadgeDato dato={dato}>{(dato.valor * 100).toFixed(0)}%</BadgeDato>
                        <span className={styles.appUsuarios}>{formatNumber(resultados.usuariosPorApp[app])}</span>
                      </div>
                    </div>
                  ))}
              </div>
            </div>

            {/* App Preferida */}
            <div className={styles.appsCard}>
              <h4 className={styles.subtitulo}>App Preferida / Más Usada</h4>
              <div className={styles.appsList}>
                {Object.entries(IPSOS_TRANSPORTE.appPreferida)
                  .filter(([app]) => app !== "Ninguna")
                  .map(([app, dato]) => (
                    <div key={app} className={styles.appItem}>
                      <div className={styles.appNombre}>{app}</div>
                      <div className={styles.appBar}>
                        <div
                          className={cx(styles.appBarFill, styles.appBarPreferida)}
                          style={{ width: `${dato.valor * 100}%` }}
                        />
                      </div>
                      <div className={styles.appStats}>
                        <BadgeDato dato={dato}>{(dato.valor * 100).toFixed(0)}%</BadgeDato>
                        <span className={styles.appUsuarios}>{formatNumber(resultados.preferenciaPorApp[app])}</span>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}

        {/* Tabla de frecuencia de uso */}
        {resultados && (
          <div className={styles.tablaContainer}>
            <h4 className={styles.subtitulo}>Frecuencia de Uso Estimada</h4>
            <table className={cx("table table-sm", styles.tablaResultados)}>
              <thead className="table-dark">
                <tr>
                  <th className={cx("text-center", styles.thNoUsa)}>No usan apps</th>
                  <th className={cx("text-center", styles.thSiUsa)}>Sí usan apps</th>
                  <th className="text-center">Frecuencia</th>
                  <th className="text-center">Usuarios</th>
                  <th className="text-center">
                    <div className={styles.headerConsumos}>
                      <span>Viajes</span>
                      <div className={styles.toggleContainer}>
                        <span className={cx(styles.toggleLabel, !verAnual && styles.toggleActive)}>mes</span>
                        <label className={styles.toggleSwitch}>
                          <input type="checkbox" checked={verAnual} onChange={(e) => setVerAnual(e.target.checked)} />
                          <span className={styles.toggleSlider} />
                        </label>
                        <span className={cx(styles.toggleLabel, verAnual && styles.toggleActive)}>año</span>
                      </div>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(resultados.desgloseFrecuencia).map(([key, data], idx) => (
                  <tr key={key}>
                    {idx === 0 && (
                      <>
                        <td rowSpan={4} className={cx("text-center align-middle", styles.celdaNo)}>
                          <div className={styles.celdaContenido}>
                            <BadgeDato dato={IPSOS_TRANSPORTE.noUsaApps}>
                              {(IPSOS_TRANSPORTE.noUsaApps.valor * 100).toFixed(0)}%
                            </BadgeDato>
                            <div className={styles.valorGrande}>{formatNumber(resultados.noUsanApps)}</div>
                          </div>
                        </td>
                        <td rowSpan={4} className={cx("text-center align-middle", styles.celdaSi)}>
                          <div className={styles.celdaContenido}>
                            <BadgeDato dato={IPSOS_TRANSPORTE.usaApps}>
                              {(IPSOS_TRANSPORTE.usaApps.valor * 100).toFixed(0)}%
                            </BadgeDato>
                            <div className={styles.valorGrande}>{formatNumber(resultados.usanApps)}</div>
                          </div>
                        </td>
                      </>
                    )}
                    <td className={styles.celdaFrecuencia}>
                      <span className={styles.frecuenciaTexto}>{data.label}</span>
                    </td>
                    <td className="text-center">{formatNumber(data.usuarios)}</td>
                    <td className="text-center">
                      <strong>{formatNumber(verAnual ? data.viajesMes * 12 : data.viajesMes)}</strong>
                    </td>
                  </tr>
                ))}
                <tr className={styles.filaTotales}>
                  <td colSpan={4} className={styles.celdaVacia} />
                  <td className={cx("text-center", styles.celdaTotal)}>
                    <strong>{formatNumber(verAnual ? resultados.totalViajesAnual : resultados.totalViajesMes)}</strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Market Share por App */}
        {resultados && (
          <div className={styles.marketShareContainer}>
            <h4 className={styles.marketShareTitulo}>Market Share - Apps de Transporte</h4>
            <div className={styles.marketShareGrid}>
              {Object.entries(resultados.mercadoPorApp).map(([app, data]) => (
                <div key={app} className={styles.marketShareCard}>
                  <div className={styles.cardNombre}>{app}</div>
                  <div className={styles.cardShare}>
                    <BadgeDato dato={data.shareDato}>{(data.share * 100).toFixed(0)}%</BadgeDato>
                  </div>
                  <div className={styles.cardMetricas}>
                    <div>
                      <span className={styles.metricaLabel}>Viajes/{verAnual ? "año" : "mes"}</span>
                      <span className={styles.metricaValor}>
                        {formatNumber(verAnual ? data.viajesMes * 12 : data.viajesMes)}
                      </span>
                    </div>
                    <div>
                      <span className={styles.metricaLabel}>Mercado/{verAnual ? "año" : "mes"}</span>
                      <span className={styles.metricaValor}>
                        {formatLocal(convertCLP(verAnual ? data.mercadoMensual * 12 : data.mercadoMensual))}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <button type="button" className={styles.simularBtn} onClick={() => setShowOportunidadModal(true)}>
              Simular Oportunidad
            </button>
          </div>
        )}

        {/* Qué valoran más */}
        <div className={styles.valoracionContainer}>
          <h4 className={styles.subtitulo}>Lo que más valoran en transporte</h4>
          <div className={styles.valoracionGrid}>
            {Object.entries(IPSOS_TRANSPORTE.valoracion).map(([item, dato]) => (
              <div key={item} className={styles.valoracionItem}>
                <span className={styles.valoracionNombre}>{item}</span>
                <BadgeDato dato={dato}>{(dato.valor * 100).toFixed(0)}%</BadgeDato>
              </div>
            ))}
          </div>
        </div>

        {/* Disclaimer */}
        <div className={styles.disclaimer}>
          <p>
            <strong>Ipsos 2025 (lámina 106, base 400):</strong> el 89,1% ha usado apps de transporte. Uber lidera con
            83,5% de instalación y 79,8% de preferencia.
          </p>
          <p>
            <strong>Supuestos comerciales:</strong> frecuencia de uso (viajes/mes) y precio por viaje, sin fuente Ipsos.
          </p>
        </div>
      </div>

      {/* Modal de Inventario */}
      {showInventarioModal && (
        <div className={styles.modalOverlay} onClick={() => setShowInventarioModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Inventario de Edificios</h3>
              <button type="button" className={styles.modalClose} onClick={() => setShowInventarioModal(false)}>
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.modalSummary}>
                <span>
                  <strong>{cantidadEdificios}</strong> edificios seleccionados
                </span>
                <span>
                  <strong>{formatNumber(totalEjecutivos)}</strong> ejecutivos
                </span>
                <button type="button" className={styles.toggleAllBtn} onClick={toggleCheckAll}>
                  {checkAll ? "Deseleccionar todos" : "Seleccionar todos"}
                </button>
              </div>
              <table className="table table-sm table-hover">
                <thead className="table-dark">
                  <tr>
                    <th className="text-center">✓</th>
                    <th className="text-center">Edificio</th>
                    <th className="text-center">Ubicación</th>
                    <th className="text-center">Pantallas</th>
                    <th className="text-center">Personas</th>
                  </tr>
                </thead>
                <tbody>
                  {edificios.map(({ id, edificio, ubicacion, personas, pantallas, espera, totems, cowork }) => (
                    <tr
                      key={id}
                      onClick={() => toggleCheck(id)}
                      className={cx(checkedBuildings[id] && "table-success")}
                      style={{ cursor: "pointer" }}
                    >
                      <td className="text-center">
                        {checkedBuildings[id] ? <ImCheckboxChecked size={16} /> : <ImCheckboxUnchecked size={16} />}
                      </td>
                      <td className="text-center">{edificio.toUpperCase()}</td>
                      <td className="text-center">{ubicacion}</td>
                      <td className="text-center">
                        {formatNumber((pantallas || 0) + (espera || 0) + (totems || 0) + (cowork || 0))}
                      </td>
                      <td className="text-center">{personas ? formatNumber(personas) : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className={styles.modalFooter}>
              <button type="button" className={styles.modalBtn} onClick={() => setShowInventarioModal(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Oportunidad */}
      {showOportunidadModal && resultados && (
        <div className={styles.modalOverlay} onClick={() => setShowOportunidadModal(false)}>
          <div className={cx(styles.modalContent, styles.modalOportunidad)} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Simulador de Oportunidad - Apps de Transporte</h3>
              <button type="button" className={styles.modalClose} onClick={() => setShowOportunidadModal(false)}>
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.simuladorContainer}>
                <div className={styles.simuladorControles}>
                  <div className={styles.controlGroup}>
                    <label>App objetivo:</label>
                    <select
                      value={appSeleccionada}
                      onChange={(e) => setAppSeleccionada(e.target.value)}
                      className="form-select form-select-sm"
                    >
                      {Object.keys(PRECIOS_VIAJE).map((app) => (
                        <option key={app} value={app}>
                          {app}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className={styles.controlGroup}>
                    <label>Tipo de viaje (supuesto):</label>
                    <select
                      value={tipoViaje}
                      onChange={(e) => setTipoViaje(e.target.value)}
                      className="form-select form-select-sm"
                    >
                      <option value="corto">
                        Corto (~{formatLocal(convertCLP(PRECIOS_VIAJE[appSeleccionada].corto.valor))})
                      </option>
                      <option value="medio">
                        Medio (~{formatLocal(convertCLP(PRECIOS_VIAJE[appSeleccionada].medio.valor))})
                      </option>
                      <option value="largo">
                        Largo (~{formatLocal(convertCLP(PRECIOS_VIAJE[appSeleccionada].largo.valor))})
                      </option>
                    </select>
                  </div>
                  <div className={styles.controlGroup}>
                    <label>% captación mercado (supuesto):</label>
                    <select
                      value={porcentajeCaptacion}
                      onChange={(e) => setPorcentajeCaptacion(Number(e.target.value))}
                      className="form-select form-select-sm"
                    >
                      {ESCENARIOS_CAPTACION_TRANSPORTE.map((p) => (
                        <option key={p.valor} value={p.valor}>
                          {p.valor}%
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {(() => {
                  const oportunidad = calcularOportunidad();
                  if (!oportunidad) return null;

                  return (
                    <div className={styles.simuladorResultados}>
                      <div className={styles.resultadoItem}>
                        <span className={styles.resultadoLabel}>Mercado total mensual:</span>
                        <span className={styles.resultadoValor}>
                          {formatLocal(convertCLP(oportunidad.mercadoTotal))}
                        </span>
                      </div>
                      <div className={styles.resultadoItem}>
                        <span className={styles.resultadoLabel}>Viajes a captar/mes:</span>
                        <span className={styles.resultadoValor}>{formatNumber(oportunidad.viajesCaptadosMes)}</span>
                      </div>
                      <div className={styles.resultadoItem}>
                        <span className={styles.resultadoLabel}>Captación al {porcentajeCaptacion}%:</span>
                        <span className={styles.resultadoValorDestacado}>
                          {formatLocal(convertCLP(oportunidad.mercadoCaptadoMensual))}/mes
                        </span>
                      </div>
                      <div className={styles.resultadoItem}>
                        <span className={styles.resultadoLabel}>Mercado captado anual:</span>
                        <span className={styles.resultadoValorDestacado}>
                          {formatLocal(convertCLP(oportunidad.mercadoCaptadoAnual))}
                        </span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button type="button" className={styles.modalBtn} onClick={() => setShowOportunidadModal(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Transporte;
